import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  ColumnDatas,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  Modal,
  Table,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import {
  DocInfo,
  PermissionPolicy,
  UserViewCountPolicy,
} from "../../types/files";
import utilsCommon from "../../shared/utils/common";
import utilsDocs from "../../shared/utils/docs";
import usePolicyStore from "../../redux/dispatcher/usePolicyStore";
import { Member, Policy } from "../../types/workgroup";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import PolicyToolTip from "../PolicyToolTip";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import ReadRequestModal from "./ReadRequestModal";
import SetPolicyPage from "../SePolicyPage";
import { nanoid } from "@reduxjs/toolkit";
import { t } from "i18next";
import { Trans } from "react-i18next";
import NameTag from "../NameTag";
import i18n from "../../locales/i18n";

import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import dayjs from "dayjs";

interface ChangeWorkgroupFileAuthModalProps {
  open: boolean;
  docInfos?: DocInfo[];
  onClose: () => void;
  onClickAfterSubmit?: () => void;
}
const ChangeWorkgroupFileAuthModal: React.FC<
  ChangeWorkgroupFileAuthModalProps
> = ({ open, docInfos, onClickAfterSubmit, onClose }) => {
  const date = new Date();
  const serverTimezone = -date.getTimezoneOffset() / 60;
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  /** Hook 정리 */
  const toast = useToast();
  const checkboxGroup = useCheckboxGroup<string>();
  const permissionListNew = useRef([] as ColumnDatas[]);
  const { setFilePolicy, setFilePolicies } = usePolicyStore();
  const {
    documentState,
    changeDocMaster,
    getContentDetail,
    getDocUserViewCount,
  } = useDocumentStore();
  const { workgroupState, getWorkgroupDetail } = useWorkgroupStore();

  /** State 정리 */
  const [newMasterName, changeNewMasterName] = useState(""); // 새 워크그룹장
  const [key, setKey] = useState(0); // state 용
  const [savedPermissionList, changeSavedPermissionList] = useState<Policy[]>(
    []
  ); // 저장된 권한 => 가장 초기 또는 저장 누르고 직후에 바뀐다.
  const [tempPermissionList, changeTempPermissionList] = useState<Policy[]>([]); // 임시 권한 => 저장 안누르면 저장되지않음.
  const [openReadRequestModal, toggleOpenReadRequestModal] = useState(false); // 열람요청 모달 여닫기 여부
  const [targetSettingUserAuth, changeTargetSettingUserAuth] =
    useState<Policy>(); //
  const [masterUserId, changeMasterUserId] = useState(""); // 문서 관리자 UserId
  const [docInfo, changeDocInfo] = useState(
    !docInfos
      ? undefined
      : docInfos?.length === 1
      ? docInfos[0]
      : docInfos?.length > 1
      ? {
          syncId: "",
          contentName: "",
          revisionNo: -1,
          checkoutUser: {
            userId: "",
            userName: null,
          },
          checkinUser: {
            userId: "",
            userName: null,
          },
          fileSize: -1,
          favoritDate: -1,
          checkin: "N",
          shareFlag: "N",
          concurrentUserId: null,
          workgroup: docInfos[0].workgroup,

          createTime: -1,
          updateTime: -1,
        }
      : undefined
  );
  const [viewCount, changeViewCount] = useState<
    undefined | UserViewCountPolicy[]
  >();
  /** useEffect 정리 */
  useEffect(() => {
    if (docInfos && docInfos.length > 1) return;
    const permission = documentState?.contentDetail?.permissionPolicyList;
    if (!permission || permission[0].syncId !== docInfo?.syncId) {
      changeMasterUserId("");
      return;
    }
    changeSavedPermissionList(permission);
    changeTempPermissionList(permission);
    changeMasterUserId(permission[0].targetValue);
  }, [
    documentState?.contentDetail?.permissionPolicyList,
    docInfo,
    documentState?.contentDetail,
  ]);
  useEffect(() => {
    if (!tempPermissionList || !workgroupState.workgroupDetail) return;
    let newDataContent = [] as ColumnDatas[];
    const masterId = tempPermissionList[0]?.targetValue;
    if (workgroupState?.workgroupDetail?.workgroup?.members)
      [...(workgroupState?.workgroupDetail?.workgroup?.members ?? [])]
        .sort((a, b) =>
          a.userId === masterId ? -1 : b.userId === masterId ? 1 : 0
        )
        .slice()
        ?.forEach((item: Member, index: number) => {
          newDataContent.push(
            getTableData(item, masterUserId === loginDto.userInfo.userId)
          );
        });
    else {
      tempPermissionList.forEach((item) =>
        newDataContent.push(
          getTableData(item, masterUserId === loginDto.userInfo.userId)
        )
      );
    }
    permissionListNew.current = newDataContent;
    setKey(key + 1);
  }, [tempPermissionList, checkboxGroup.selected]);
  useEffect(() => {
    if (docInfo === undefined || (docInfos && docInfos?.length > 1)) return;
    getWorkgroupDetail({ workgroupId: docInfo?.workgroup.workgroupId });

    getContentDetail({
      syncId: docInfo?.syncId,
      workgroupId: docInfo?.workgroup.workgroupId,
    });
    getDocUserViewCount({
      syncId: docInfo?.syncId,
      workgroupId: docInfo?.workgroup.workgroupId,
    }).then((resp: any) => changeViewCount(resp.payload));
  }, [docInfo]);
  useEffect(() => {
    changeDocInfo(
      !docInfos
        ? undefined
        : docInfos?.length === 1
        ? docInfos[0]
        : docInfos?.length > 1
        ? {
            ...docInfos[0],
          }
        : undefined
    );
    if (docInfos && docInfos?.length > 1) {
      const permission = [
        {
          changeCode: null,
          uniqueId: "MASTER",
          workgroupCode: docInfos[0].workgroup.workgroupId,
          syncId: docInfos[0].syncId,
          targetType: "USER",
          targetValue: loginDto.userInfo.userId,
          authType: "LOGIN",
          policyType: "DOCUMENT",
          policyCode: "",
          updateTime: 0,
          policy: {
            startTime: 0,
            allowDays: 0,
            allowDaysTimeZone: serverTimezone,
            allowViewCount: 0,
            currentViewCount: 0,
            allowDownloadCount: 0,
            currentDownloadCount: 0,
            drmPerm: null,
            webView: true,
            revision: false,
            download: true,
            wrapping: false,
            additionalViewPermission: "NONE",
            fileShareLinkPolicy: null,
            notificationPolicy: null,
          },
          companyName: loginDto.ecoUserInfo.deptName,
          deptName: loginDto.userInfo.deptName,
          userType: loginDto.ecoUserInfo.userType,
          status: loginDto.ecoUserInfo.status,
          userName: loginDto.userInfo.userName,
          phoneNumber: "",
        },
        workgroupState.workgroupDetail.policy,
      ];
      changeSavedPermissionList(permission);
      changeTempPermissionList(permission);
      changeMasterUserId(permission[0].targetValue);
    }
  }, [docInfos]);

  /** 함수 호출 */
  const handleDoAction = (
    key: string,
    targetUserId?: string,
    indPolicy?: PermissionPolicy
  ) => {
    let nextPermission = tempPermissionList;
    const targetUserIds = targetUserId
      ? [targetUserId]
      : checkboxGroup.selected;
    setKey((key1) => key1 + 1);
    if (key === "MASTER" && targetUserId) {
      changeNewMasterName(targetUserId);
    }
    if (key === "WORKGROUP") {
      nextPermission = tempPermissionList.filter(
        (item: Policy) =>
          item.targetValue && !targetUserIds.includes(item.targetValue)
      );
    }
    if (key === "NONE") {
      nextPermission = [
        ...(tempPermissionList.filter(
          (item: Policy) =>
            item.targetValue && !targetUserIds.includes(item.targetValue)
        ) ?? []),
        ...(targetUserIds?.map((item) => {
          const savedPermission =
            tempPermissionList.filter(
              (item1: Policy) => item1.targetValue && item === item1.targetValue
            ) ?? [];
          return {
            ...savedPermission[0],
            changeCode: "CREATE",
            uniqueId: null,
            workgroupCode: docInfo?.workgroup.workgroupId,
            syncId: docInfo?.syncId,
            targetType: "USER",
            targetValue: item,
            authType: "DENY",
            policyType: "DOCUMENT",
            policy: {
              startTime: 0,
              allowDays: 61,
              allowViewCount: 0,
              allowDaysTimeZone: serverTimezone,
              currentViewCount: 0,
              allowDownloadCount: 0,
              currentDownloadCount: 0,
              drmPerm: [],
              webView: true,
              revision: true,
              download: true,
              wrapping: false,
            },
          };
        }) ?? []),
      ];
    }
    if (key === "PERSONAL") {
      if (indPolicy) {
        nextPermission = [
          ...(tempPermissionList.filter(
            (item: Policy) =>
              item.targetValue && !targetUserIds.includes(item.targetValue)
          ) ?? []),
          ...targetUserIds?.map((item) => {
            const savedPermission =
              tempPermissionList.filter(
                (item1: Policy) =>
                  item1.targetValue && item === item1.targetValue
              ) ?? [];
            return {
              ...savedPermission[0],
              changeCode: "CREATE",
              uniqueId: null,
              workgroupCode: docInfo?.workgroup.workgroupId,
              syncId: docInfo?.syncId,
              targetType: "USER",
              targetValue: item,
              authType: "LOGIN",
              policyType: "DOCUMENT",
              policy: indPolicy,
              updateUser: loginDto.userInfo.userId,
            };
          }),
        ];
        changeTargetSettingUserAuth(undefined);
      } else {
        changeTargetSettingUserAuth(
          tempPermissionList.filter(
            (item) =>
              item.targetValue &&
              item.authType !== "DENY" &&
              targetUserIds.includes(item.targetValue) &&
              targetUserIds.length === 1
          )[0] ?? {
            changeCode: "CREATE",
            uniqueId: null,
            workgroupCode: docInfo?.workgroup.workgroupId,
            syncId: docInfo?.syncId,
            targetType: "USER",
            targetValue: targetUserId,
            authType: "LOGIN",
            policyType: "DOCUMENT",
            policy: {
              ...savedPermissionList?.filter(
                (item: Policy) => item.targetType === "WORKGROUP"
              )[0].policy,
              currentViewCount:
                savedPermissionList?.filter(
                  (item: Policy) => item.targetType === "WORKGROUP"
                )[0].policy?.allowViewCount ?? 0,
            },
            updateUser: loginDto.userInfo.userId,
          }
        );
        return;
      }
    }
    if (nextPermission[0].uniqueId === "MASTER")
      changeTempPermissionList(nextPermission);
    else changeTempPermissionList([savedPermissionList[0], ...nextPermission]);
  };
  /** 권한 저장 */
  const handleSavePermission = () => {
    if (!docInfo?.syncId) return;
    docInfos?.forEach(async (doc, index) => {
      const permissionUserList =
        tempPermissionList?.map((item) => item.targetValue) ?? [];
      const existUserList =
        savedPermissionList?.map((item: Policy) => item.targetValue) ?? [];
      let newNextPermission = [] as Policy[];
      newNextPermission = newNextPermission.concat(
        tempPermissionList
          .filter((item) => !existUserList.includes(item.targetValue))
          .map((item) => {
            return {
              ...item,
              syncId: doc.syncId,
              changeCode: "CREATE",
              updateUser: loginDto.userInfo.userId,
            };
          })
      );
      newNextPermission = newNextPermission.concat(
        tempPermissionList
          .filter(
            (item) =>
              item.changeCode !== null &&
              existUserList.includes(item.targetValue)
          )
          .map((item: Policy) => {
            return {
              ...item,
              changeCode: "UPDATE",
              uniqueId: savedPermissionList.filter(
                (item1: Policy) => item.targetValue === item1.targetValue
              )[0].uniqueId,
              policy: item.policy
                ? {
                    ...item.policy,
                    fileShareLinkPolicy: null,
                    notificationPolicy: null,
                  }
                : undefined,
            };
          })
      );
      newNextPermission = newNextPermission.concat(
        savedPermissionList
          .filter(
            (item: Policy) => !permissionUserList?.includes(item.targetValue)
          )
          .map((item: Policy) => {
            return {
              ...item,
              changeCode: "DELETE",
              uniqueId: savedPermissionList.filter(
                (item1: Policy) => item.targetValue === item1.targetValue
              )[0].uniqueId,
            };
          })
      );
      let resp = await (docInfos.length === 1
        ? setFilePolicy({
            syncId: doc?.syncId,
            workgroupId: doc?.workgroup.workgroupId,
            setPolicyDTO: newNextPermission,
          })
        : setFilePolicies({
            syncId: doc?.syncId,
            workgroupId: doc?.workgroup.workgroupId,
            setPolicyDTO: newNextPermission,
          }));
      if (resp.payload && docInfos.length - 1 === index) {
        toast.toastMsg(nanoid(), t("file.alert.changeAuth"), "success");
      }
    });
  };
  const renderAuthItem = (
    targetAuth: "MASTER" | "PERSONAL" | "WORKGROUP" | "NONE",
    isMaster: boolean,
    row: Member | Policy,
    targetType?: Policy
  ) => {
    const workgroupMasterId =
      workgroupState.workgroupDetail?.workgroup?.managerId;
    const isDropdownDisabled =
      row.status !== 1 ||
      targetAuth === "MASTER" ||
      (!isMaster && workgroupMasterId !== loginDto.userInfo.userId);

    return (
      <Box
        classes="mg ml-auto"
        height={52}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Dropdown
          placeholder=" "
          btnStyle="link"
          value={
            targetAuth === "MASTER"
              ? t("file.policy.master")
              : targetAuth === "WORKGROUP"
              ? t("file.policy.workgroup")
              : targetAuth === "NONE"
              ? t("file.policy.none")
              : t("file.policy.ind")
          }
          buttonClick={key}
          buttonWidth={200}
          positionStrategy="fixed"
          classes="docAuthDropdown tooltip-disable"
          width="fit-content"
          iconPos={isDropdownDisabled ? "left" : "right"}
          dropdownDisabled={isDropdownDisabled}
          leadingIcon={
            <Icons
              variant="fill"
              label="dot"
              stroke={utilsCommon.getAuthColor(targetAuth)}
            />
          }
        >
          {(isMaster
            ? [
                { key: "MASTER", label: t("file.policy.master") },
                { key: "WORKGROUP", label: t("file.policy.workgroup") },
                { key: "PERSONAL", label: t("file.policy.ind") },
                { key: "NONE", label: t("file.policy.none") },
              ]
            : [{ key: "MASTER", label: t("file.policy.master") }]
          )
            .filter(
              (item1) =>
                docInfo?.workgroup.workgroupId !== "NONEWORKGROUP" ||
                ["PERSONAL", "NONE"].includes(item1.key)
            )
            .map((item) => (
              <DropdownItem
                label={item.label}
                isSVGMargin={false}
                checkColor={targetAuth === item.key}
                type="check"
                checked={targetAuth === item.key}
                onClick={() =>
                  handleDoAction(
                    item.key,
                    "userId" in row ? row.userId : row.targetValue
                  )
                }
                trailingIcon={
                  item.key === "PERSONAL" ? (
                    <Icons variant="line" label="setting" classes="mg ml-10" />
                  ) : undefined
                }
                frontIcon={
                  <Box
                    minWidth={20}
                    style={{
                      marginInlineStart: "2px",
                      marginInlineEnd: "3px",
                    }}
                  >
                    <Icons
                      variant="fill"
                      label="dot"
                      stroke={utilsCommon.getAuthColor(item.key)}
                    />
                  </Box>
                }
              />
            ))}
        </Dropdown>
      </Box>
    );
  };
  const handleUpdateMasterUser = async () => {
    if (!docInfos) return;
    const mergeDocInfos = [...(docInfos ?? [])]?.filter((item) => !!item);
    mergeDocInfos.forEach(async (doc, index) => {
      if (!doc) return;
      const resp: any = await changeDocMaster({
        syncId: doc?.syncId,
        fromUserId: loginDto.userInfo.userId,
        toUserId: newMasterName,
        rev: doc?.revisionNo,
      });
      if (resp.payload) {
        if (index < mergeDocInfos.length - 1) return;
        if (mergeDocInfos.length === 1 && doc) {
          getContentDetail({
            syncId: doc?.syncId,
            workgroupId: doc?.workgroup.workgroupId,
          });
          const nextPermission = tempPermissionList.map((item: Policy) =>
            item.uniqueId === "MASTER"
              ? { ...item, targetValue: newMasterName }
              : item
          );
          changeTempPermissionList(nextPermission);
        }
        toast.toastMsg(
          nanoid(),
          t("file.alert.changeFileOwnerSuccess"),
          "success"
        );
        changeNewMasterName("");
        checkboxGroup.handleAllClick([]);
        onClose();
      } else {
        toast.toastMsg(
          nanoid(),
          resp?.error?.message ?? t("file.alert.changemasterFail"),
          "error"
        );
      }
    });
  };
  /** 권한 초기화 */
  const handleInitAuth = () => {
    let initPermission = tempPermissionList?.filter(
      (item) =>
        item?.targetValue && !checkboxGroup.isSelected(item?.targetValue)
    );
    changeTempPermissionList([
      ...initPermission,
      ...savedPermissionList?.filter(
        (item: Policy) =>
          item?.targetValue && checkboxGroup.isSelected(item?.targetValue)
      ),
    ]);
    toast.toastMsg(nanoid(), t("workgroup.authReset"), "success");
  };
  const getTableData = (row: Member | Policy, isMaster: boolean) => {
    const userId =
      "userId" in row
        ? row?.userId ?? ""
        : "targetValue" in row
        ? row?.targetValue ?? ""
        : "";

    let targetType = tempPermissionList?.filter(
      (item: Policy) => item.targetValue === userId
    );
    const targetAuth =
      !targetType ||
      targetType?.length === 0 ||
      targetType[0].targetType === "WORKGROUP"
        ? "WORKGROUP"
        : targetType[0].uniqueId === "MASTER"
        ? "MASTER"
        : targetType[0].authType === "DENY"
        ? "NONE"
        : "PERSONAL";
    return {
      data: [
        {
          columnText: (
            <Box alignItems="center">
              {"userId" in row && (
                <NameTag
                  user={row}
                  width={"100%"}
                  withoutUserId={false}
                  height={52}
                />
              )}
            </Box>
          ),
        },
        {
          columnText: renderAuthItem(targetAuth, isMaster, row, targetType[0]),
        },
      ],
      id: userId,
      checkBoxClick:
        isMaster && userId !== masterUserId && row.status === 1
          ? () => {
              checkboxGroup.handleClick(userId);
              setKey(key + 1);
            }
          : undefined,
      rowTooltip: (rowEl) => {
        let workgroupPolicy = savedPermissionList?.filter(
          (item: Policy) => item.targetType === "WORKGROUP"
        )[0];
        let userPolicy = savedPermissionList?.filter(
          (item: Policy) => "userId" in row && item.targetValue === row.userId
        )[0];
        const targetViewCount =
          viewCount?.filter(
            (item) =>
              item.userId ===
              (row.uniqueId ?? "userId" in row ? (row as Member).userId : "")
          )[0]?.currentViewCount ?? 0;
        return (
          <PolicyToolTip
            showTooltip
            targetViewCount={targetViewCount}
            policy={
              targetType[0]
                ? {
                    ...targetType[0],
                    policy: {
                      ...targetType[0].policy,
                      startTime:
                        userPolicy &&
                        JSON.stringify(userPolicy) ===
                          JSON.stringify(targetType[0])
                          ? targetType[0].policy?.startTime
                          : dayjs().valueOf(),
                      allowViewCount:
                        targetType[0]?.policy?.allowViewCount ?? 0,
                      currentViewCount:
                        targetType[0]?.policy?.currentViewCount ?? 0,
                    },
                  }
                : workgroupPolicy
            }
            buttonClick={key}
            targetAuth={targetAuth}
            classes="mg ml-auto"
            docInfo={docInfo}
          >
            {rowEl}
          </PolicyToolTip>
        );
      },
    } as ColumnDatas;
  };
  const renderUpdatePersonalAuthModal = () => {
    const targetuser = docInfo?.workgroup?.members.filter(
      (item) => item.userId === targetSettingUserAuth?.targetValue
    )[0];
    return (
      <Modal
        width={540}
        bodyHeight={556}
        open={!!targetSettingUserAuth}
        onClose={() => changeTargetSettingUserAuth(undefined)}
        title={
          <Box alignItems="center">
            {!!targetuser && "‘"}
            {targetuser && (
              <Tooltip message={targetuser.userName ?? targetuser.userId}>
                <Typography ellipsis maxWidth={272} type="h2">
                  {targetuser.userName ?? targetuser.userId}
                </Typography>
              </Tooltip>
            )}
            {!!targetuser
              ? "’ " + t("common.setAuth")
              : t("sendingFile.setAuth.changeAllTitle")}
          </Box>
        }
        isBack
        onBack={() => changeTargetSettingUserAuth(undefined)}
        footer={
          <Box>
            <Button
              btnStyle="fill"
              label={t("common.cancel")}
              type={"secondary"}
              onClick={() => changeTargetSettingUserAuth(undefined)}
            />
            <Button
              btnStyle="fill"
              label={t("common.apply")}
              state={
                targetSettingUserAuth?.policy?.allowDays !== undefined &&
                targetSettingUserAuth?.policy?.allowDays > 0 &&
                dayjs(
                  targetSettingUserAuth &&
                    // !!targetSettingUserAuth.uniqueId &&
                    targetSettingUserAuth.targetType === "USER"
                    ? dayjs(targetSettingUserAuth.policy?.startTime)
                        .set("hour", 0)
                        .valueOf()
                    : docInfo?.createTime
                )
                  .add(targetSettingUserAuth?.policy?.allowDays - 1, "d")
                  .endOf("day")
                  .isBefore(dayjs().startOf("day"), "day")
                  ? "disable"
                  : "default"
              }
              onClick={() => {
                handleDoAction(
                  "PERSONAL",
                  targetSettingUserAuth?.targetValue ?? "",
                  targetSettingUserAuth?.policy
                );
              }}
            />
          </Box>
        }
        body={
          <SetPolicyPage
            targetSettingUserAuth={targetSettingUserAuth}
            docInfo={docInfo}
            policy={targetSettingUserAuth?.policy}
            isInd
            changePolicy={(newPolicy) => {
              targetSettingUserAuth &&
                changeTargetSettingUserAuth({
                  ...targetSettingUserAuth,
                  policy: newPolicy,
                });
            }}
          />
        }
      />
    );
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      width={540}
      isCustom
      classes="fileAuth"
    >
      {newMasterName && (
        <Modal
          open={newMasterName !== ""}
          onClose={() => changeNewMasterName("")}
          title={t("file.action.changeOwner")}
          bodyMsg={t("file.alert.changemasterConfirm")}
          width={540}
          footer={
            <Box>
              <Button
                btnStyle="fill"
                label={t("common.cancel")}
                onClick={() => changeNewMasterName("")}
                type="secondary"
              />
              <Button
                btnStyle="fill"
                label={t("common.change")}
                onClick={handleUpdateMasterUser}
              />
            </Box>
          }
        />
      )}
      {openReadRequestModal && (
        <ReadRequestModal
          open
          onClose={onClose}
          onBack={() => toggleOpenReadRequestModal(false)}
          docInfo={docInfo}
        />
      )}
      {!!targetSettingUserAuth && renderUpdatePersonalAuthModal()}
      <Box alignItems="center" height={90} classes="fileAuth-header">
        <Box
          direction="column"
          width={"calc(100% - 20px)"}
          boxSizing="border-box"
        >
          <Typography type="h2">{t("file.action.auth")}</Typography>
          <Box
            classes="mg mt-4"
            width={
              docInfos && docInfos?.length > 1 ? "calc(100%b - 134px)" : "100%"
            }
            boxSizing="border-box"
          >
            {utilsDocs.renderDocIcon(docInfo?.contentName, 24)}
            <Tooltip
              message={docInfo?.contentName}
              classes="mg ml-8"
              maxWidth={
                docInfos && docInfos?.length > 1
                  ? `calc(100% - ${i18n.language.includes("en") ? 115 : 80}px)`
                  : "calc(100% - 32px)"
              }
            >
              <Typography ellipsis type="body2_rg">
                {docInfo?.contentName}
              </Typography>
            </Tooltip>
            {docInfos && docInfos?.length > 1 && (
              <Typography ellipsis type="body2_rg" classes="mg ml-4">
                {t("common.andNOthers", { num: docInfos?.length - 1 })}
              </Typography>
            )}
          </Box>
        </Box>
        <Box onClick={onClose} classes="cursor pointer mg mt-4 ml-auto">
          <Icons variant="line" label="closeLarge" />
        </Box>
      </Box>
      <Box height={422} direction="column">
        <Box height={30} classes="pd pl-28 pr-28">
          <Box alignItems="center">
            <Typography type="subtitle2">
              {checkboxGroup.selected.length === 0 ? (
                <Trans
                  i18nKey={"common.memberWithCount"}
                  values={{
                    count:
                      workgroupState?.workgroupDetail?.workgroup?.members
                        ?.length ?? 0,
                  }}
                  components={{
                    1: (
                      <Typography
                        type="subtitle2"
                        exactColor={getColor("COLOR_TEXT_BRAND")}
                      />
                    ),
                  }}
                />
              ) : (
                <Trans
                  i18nKey="common.chosenMember"
                  values={{
                    count: checkboxGroup.selected?.length,
                  }}
                  components={{
                    1: (
                      <Typography
                        type="subtitle2"
                        exactColor={getColor("COLOR_TEXT_BRAND")}
                      />
                    ),
                  }}
                />
              )}
            </Typography>
          </Box>
          <Box classes="mg ml-auto">
            <Dropdown
              value={t("common.setAuth")}
              btnStyle="link"
              btnSize="lg"
              iconPos="left"
              placeholder={" "}
              buttonClick={key}
              leadingIcon={<Icons variant="line" label="shield" isOn />}
              buttonWidth={200}
              align={"right"}
              disabled={checkboxGroup.selected.length === 0}
            >
              {[
                { key: "WORKGROUP", label: t("file.policy.workgroup") },
                { key: "PERSONAL", label: t("file.policy.ind") },
                { key: "NONE", label: t("file.policy.none") },
              ]
                .filter(
                  (item1) =>
                    docInfo?.workgroup.workgroupId !== "NONEWORKGROUP" ||
                    ["PERSONAL", "NONE"].includes(item1.key)
                )
                .map((item) => (
                  <DropdownItem
                    label={item.label}
                    isSVGMargin={false}
                    onClick={() => handleDoAction(item.key)}
                    trailingIcon={
                      item.key === "PERSONAL" ? (
                        <Icons
                          variant="line"
                          label="setting"
                          classes="mg ml-10"
                        />
                      ) : undefined
                    }
                    frontIcon={
                      <Box
                        minWidth={20}
                        style={{
                          marginInlineStart: "2px",
                          marginInlineEnd: "3px",
                        }}
                      >
                        <Icons
                          variant="fill"
                          label="dot"
                          stroke={utilsCommon.getAuthColor(item.key)}
                        />
                      </Box>
                    }
                  />
                ))}
            </Dropdown>
            <Button
              label={t("common.initAuth")}
              btnStyle="link"
              size="lg"
              state={
                checkboxGroup.selected.length === 0 ? "disable" : "default"
              }
              onClick={handleInitAuth}
              leadingIcon={<Icons variant="line" label="redoLeft" isOn />}
            />
          </Box>
        </Box>
        <Table
          width="calc(100% - 56px)"
          clickIds={checkboxGroup.selected}
          totalCount={
            docInfo?.workgroup?.members?.filter(
              (item1) =>
                item1.userId !== loginDto.userInfo.userId && item1.status === 1
            ).length
          }
          checkBoxClick={
            masterUserId === loginDto.userInfo.userId
              ? () =>
                  checkboxGroup.handleAllClick(
                    checkboxGroup.selected.length ===
                      (
                        workgroupState?.workgroupDetail?.workgroup?.members?.filter(
                          (item1: Member) =>
                            item1.userId !== loginDto.userInfo.userId &&
                            item1.status === 1
                        ) ?? []
                      )?.length
                      ? []
                      : workgroupState?.workgroupDetail?.workgroup?.members
                          ?.filter(
                            (item1: Member) =>
                              item1.userId !== loginDto.userInfo.userId &&
                              item1.status === 1
                          )
                          ?.map((item: Member) => item.userId) ?? []
                  )
              : undefined
          }
          height="345px"
          classes="fileAuth-table pd pl-28 pr-28 mg mt-4"
          columnHeight="md"
          header={[
            {
              headerText: t("common.idAndName"),
              width: i18n.language.includes("en")
                ? "calc(100% - 256px)"
                : "calc(100% - 206px)",
            },
            {
              headerText: "",
              width: i18n.language.includes("en") ? "270px" : "162px",
            },
          ]}
          data={permissionListNew.current}
          endReached={() => console.log("!")}
        />
      </Box>
      <Box height={88} alignItems="center">
        {docInfos &&
          docInfos?.length === 1 &&
          docInfos[0]?.myPolicy?.uniqueId !== "ONLYVIEW" && (
            <Button
              btnStyle="link"
              label={t("file.action.sendReadRequest")}
              leadingIcon={<Icons variant="line" label="alert" />}
              classes="mg ml-20"
              onClick={() => toggleOpenReadRequestModal(true)}
            />
          )}
        {masterUserId === loginDto.userInfo.userId && (
          <Box classes="mg ml-auto mr-28">
            <Button
              btnStyle="fill"
              label={t("common.cancel")}
              type="secondary"
              onClick={onClose}
            />
            <Button
              btnStyle="fill"
              label={t("common.save")}
              classes="mg ml-8"
              state={
                !docInfos ||
                (docInfos?.length === 1 &&
                  JSON.stringify(savedPermissionList) ===
                    JSON.stringify(tempPermissionList))
                  ? "disable"
                  : "default"
              }
              onClick={() => {
                handleSavePermission();
                onClickAfterSubmit && onClickAfterSubmit();
                onClose();
              }}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};
export default ChangeWorkgroupFileAuthModal;
